import { memo, useState, useCallback, useEffect, useRef } from 'react';
import { Handle, Position } from 'reactflow';
import { 
  Volume2, X, Loader2, CheckCircle, AlertCircle, 
  Play, Pause, Download, Upload, Mic, Wand2, Users, Eye
} from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { blobToFile } from '../../utils/mediaUtils';
import { useWorkflowStore } from '../../store/workflowStore';
import { useNodeExecution } from '../../hooks/useNodeExecution';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import NodeError from './NodeError';
import NodeResetButton from './NodeResetButton';
import {
  ReconfigureButton, ReconfigureNotice, RegenerateButton, useReconfigure,
} from './ReconfigureControls';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import { setSourcePickerHandler } from '../../utils/sourcePickerBridge';
import SendToTimelineButton from '../SendToTimelineButton';
import PromptField from '../studio/PromptField';
import NodeRequirements from './NodeRequirements';
import { requirementsTitle } from '../../utils/nodeRequirements';
import { SPEAKERS, qwenRequirements, validSpeaker } from '../../utils/qwen3Tts';

// Supported languages
const LANGUAGES = [
  { id: 'auto', label: 'Auto-detectar' },
  { id: 'Spanish', label: 'Español' },
  { id: 'English', label: 'Inglés' },
  { id: 'Chinese', label: 'Chino' },
  { id: 'Japanese', label: 'Japonés' },
  { id: 'Korean', label: 'Coreano' },
  { id: 'German', label: 'Alemán' },
  { id: 'French', label: 'Francés' },
  { id: 'Italian', label: 'Italiano' },
  { id: 'Portuguese', label: 'Portugués' },
  { id: 'Russian', label: 'Ruso' },
];

// Mode configurations
const MODES = [
  { 
    id: 'custom_voice', 
    label: 'Voz Preset', 
    icon: Users, 
    description: 'Usa voces preestablecidas',
    color: '#10b981'
  },
  { 
    id: 'voice_clone', 
    label: 'Clonar Voz', 
    icon: Mic, 
    description: 'Clona una voz de audio (3s+)',
    color: '#8b5cf6'
  },
  { 
    id: 'voice_design', 
    label: 'Diseñar Voz', 
    icon: Wand2, 
    description: 'Crea voz desde descripción',
    color: '#f59e0b'
  },
];

const Qwen3TTSNode = memo(({ id, data, selected }) => {
  // State
  const [chronometer, setChronometer] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Form state
  const [mode, setMode] = useState(data.mode || 'custom_voice');
  const [text, setText] = useState(data.text || '');
  const [language, setLanguage] = useState(data.language || 'auto');
  const [speaker, setSpeaker] = useState(() => validSpeaker(data.speaker));
  const [voiceDescription, setVoiceDescription] = useState(data.voiceDescription || '');
  const [referenceAudio, setReferenceAudio] = useState(null);
  const [referenceText, setReferenceText] = useState(data.referenceText || '');
  const [styleInstruction, setStyleInstruction] = useState(data.styleInstruction || '');
  
  const fileInputRef = useRef(null);
  
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const addAsset = useWorkflowStore((state) => state.addAsset);
  const getInputForHandle = useWorkflowStore((state) => state.getInputForHandle);
  const openSourcePicker = useWorkflowStore((state) => state.openSourcePicker);

  const handleSourcePickAudio = useCallback(() => {
    setSourcePickerHandler((result) => {
      if (result.file) {
        setReferenceAudio(result.file);
      } else if (result.url) {
        setReferenceAudio({ name: result.fileName, url: result.url, fromGallery: true });
      }
    });
    openSourcePicker(id, ['audio'], 'audio/wav,audio/mp3,audio/mpeg,audio/ogg');
  }, [id, openSourcePicker]);
  
  const { processNode, status: execStatus, error: execError, reset: resetExec, insufficientCredits } = useNodeExecution();
  const isProcessing = execStatus === 'uploading' || execStatus === 'queued' || execStatus === 'processing';
  const { openAudioPreview } = useMediaPreview();

  // Chronometer effect
  useEffect(() => {
    let interval;
    if (isProcessing) {
      setChronometer(0);
      interval = setInterval(() => {
        setChronometer((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isProcessing]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Check for connected audio input (for voice cloning)
  const connectedAudio = getInputForHandle(id, 'inputAudio');

  const togglePlay = useCallback(() => {
    const audio = document.getElementById(`qwen3-tts-audio-${id}`);
    if (audio) {
      if (isPlaying) {
        audio.pause();
      } else {
        audio.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [id, isPlaying]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setReferenceAudio({ file, preview: URL.createObjectURL(file), name: file.name });
    }
  };

  const handleProcess = useCallback(async () => {
    if (!text.trim()) return false;
    resetExec();

    const params = {
      text: text.trim(),
      mode,
      language,
    };
    const files = [];

    if (mode === 'custom_voice') {
      params.speaker = validSpeaker(speaker);
    } else if (mode === 'voice_clone') {
      const isHttp = (u) => /^https?:\/\//i.test(String(u || ''));
      if (referenceAudio?.file) {
        files.push({ key: 'reference_audio_url', file: referenceAudio.file || referenceAudio });
      } else if (referenceAudio?.preview) {
        const f = await blobToFile(referenceAudio.preview, referenceAudio.name || 'reference.wav', 'audio/wav');
        if (f) files.push({ key: 'reference_audio_url', file: f });
      } else if (isHttp(referenceAudio?.url)) {
        // ND-1 · audio elegido de la Galería: ya está en la nube, va la URL.
        params.reference_audio_url = referenceAudio.url;
      } else if (isHttp(connectedAudio?.outputUrl)) {
        params.reference_audio_url = connectedAudio.outputUrl;
      } else if (connectedAudio?.outputUrl) {
        const f = await blobToFile(connectedAudio.outputUrl, 'reference.wav', 'audio/wav');
        if (f) files.push({ key: 'reference_audio_url', file: f });
      }
      if (!files.length && !params.reference_audio_url) return false;  // sin voz para clonar
      if (referenceText.trim()) params.reference_text = referenceText.trim();
    } else if (mode === 'voice_design') {
      if (!voiceDescription.trim()) return false;
      params.voice_description = voiceDescription.trim();
    }
    if (styleInstruction.trim()) params.style_instruction = styleInstruction.trim();

    const result = await processNode({ resultType: 'qwen3_tts', files, params });

    if (result?.outputUrl) {
      updateNodeData(id, {
        hasOutput: true,
        outputUrl: result.outputUrl,
        assetId: result.assetId ?? null,
        outputType: 'audio',
        processedAt: new Date().toISOString(),
        mode, text, language,
        speaker: mode === 'custom_voice' ? speaker : null,
        voiceDescription: mode === 'voice_design' ? voiceDescription : null,
      });
      addAsset({
        id: `asset_${Date.now()}`,
        type: 'audio',
        tool: 'qwen3-tts',
        filename: `tts_${text.slice(0, 20)}`,
        url: result.outputUrl,
        createdAt: new Date().toISOString(),
      });
      return true;
    }
    return false;
  }, [
    id, text, mode, language, speaker, voiceDescription, referenceAudio,
    referenceText, styleInstruction, connectedAudio, updateNodeData,
    addAsset, processNode, resetExec
  ]);

  // Regenerar descarta el resultado; volver a configurar lo conserva.
  const { reconfigure, setReconfigure, showForm } = useReconfigure(data);
  const handleRegenerate = useCallback(() => {
    setReconfigure(false);
    updateNodeData(id, { hasOutput: false, outputUrl: null });
  }, [id, updateNodeData, setReconfigure]);
  const { isInQueue, isMyTurn } = useWorkflowTrigger(id, handleProcess);

  const currentMode = MODES.find(m => m.id === mode);
  const ModeIcon = currentMode?.icon || Volume2;

  // NR-1 · qué le falta al nodo para generar (se muestra debajo del botón)
  const hasReference = !!(referenceAudio?.file || referenceAudio?.preview || referenceAudio?.url
    || connectedAudio?.outputUrl);
  const { missing: runMissing, optional: runOptional } = qwenRequirements({
    mode, text, speaker, voiceDescription, hasReference,
    referenceConnected: !!connectedAudio && !connectedAudio.outputUrl,
  });

  return (
    <div className={`node-container qwen3tts-node ${selected ? 'selected' : ''} ${isInQueue ? 'workflow-queued' : ''} ${isMyTurn ? 'workflow-active' : ''}`} data-testid="qwen3tts-node">
      <NodeResetButton nodeId={id} hasOutput={!!data.hasOutput} />
      {reconfigure && (
        <ReconfigureNotice
          previewUrl={data.thumbnailUrl || null}
          onCancel={() => setReconfigure(false)}
          testId="qwen3tts-reconfigure-notice"
        />
      )}
      {/* Input Handle for voice cloning */}
      <Handle
        type="target"
        position={Position.Left}
        id="inputAudio"
        className="node-handle input-handle audio-handle"
        style={{ top: '30%' }}
      />
      
      {/* Header */}
      <div className="node-header qwen3tts-header" style={{ borderColor: currentMode?.color }}>
        <div className="node-header-left">
          <Volume2 size={16} />
          <span>Qwen3 TTS</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)}>
          <X size={14} />
        </button>
      </div>
      
      {/* Content */}
      <div className="node-content qwen3tts-content">
        {/* Processing State */}
        {isProcessing ? (
          <div className="node-processing">
            <div className="node-chronometer">
              {formatTime(chronometer)}
            </div>
            <div className="node-processing-info">
              <Loader2 size={16} className="animate-spin" />
              <span>Generando voz...</span>
            </div>
            <p className="node-processing-hint">
              {mode === 'custom_voice' && `Voz: ${speaker}`}
              {mode === 'voice_clone' && 'Clonando voz...'}
              {mode === 'voice_design' && 'Diseñando voz...'}
            </p>
          </div>
        ) : !showForm ? (
          <div className="node-result qwen3tts-result">
            <audio 
              id={`qwen3-tts-audio-${id}`}
              src={data.outputUrl} 
              onEnded={() => setIsPlaying(false)}
            />
            <div className="qwen3tts-player">
              <button className="qwen3tts-play-btn" onClick={togglePlay}>
                {isPlaying ? <Pause size={24} /> : <Play size={24} />}
              </button>
              <div className="qwen3tts-waveform">
                {[...Array(12)].map((_, i) => (
                  <div 
                    key={`wave-${i}`} 
                    className={`qwen3tts-wave-bar ${isPlaying ? 'animate' : ''}`}
                    style={{ animationDelay: `${i * 0.1}s` }}
                  />
                ))}
              </div>
            </div>
            <div className="qwen3tts-result-info">
              <span className="qwen3tts-mode-badge" style={{ backgroundColor: currentMode?.color + '20', color: currentMode?.color }}>
                <ModeIcon size={12} />
                {currentMode?.label}
              </span>
            </div>
            <div className="node-result-actions">
              <RegenerateButton onClick={handleRegenerate} testId="qwen3tts-regen-btn" />
              <ReconfigureButton onClick={() => setReconfigure(true)} testId="qwen3tts-reconfigure-btn" />
              <button 
                className="node-mini-preview-btn"
                onClick={() => openAudioPreview(data.outputUrl, 'Qwen3 TTS - Audio')}
              >
                <Eye size={12} />
                Ver
              </button>
              <button 
                className="node-mini-download-btn"
                onClick={(e) => { e.stopPropagation(); downloadFile(data.outputUrl, `tts_${Date.now()}.wav`); }}
              >
                <Download size={12} />
                Descargar
              </button>
              <SendToTimelineButton assetId={data.assetId} nodeId={id} url={data.outputUrl} kind="audio" label="qwen3tts" testIdPrefix="qwen3tts-timeline" />
            </div>
            <div className="node-result-badge">
              <CheckCircle size={12} />
              <span>Audio generado</span>
            </div>
          </div>
        ) : (
          <>
            {/* Mode Selection */}
            <div className="qwen3tts-mode-section">
              <label>Modo</label>
              <div className="qwen3tts-mode-grid">
                {MODES.map((m) => {
                  const Icon = m.icon;
                  return (
                    <button
                      key={m.id}
                      className={`qwen3tts-mode-btn ${mode === m.id ? 'active' : ''}`}
                      onClick={() => setMode(m.id)}
                      style={{ 
                        borderColor: mode === m.id ? m.color : 'transparent',
                        backgroundColor: mode === m.id ? m.color + '15' : 'transparent'
                      }}
                      title={m.description}
                    >
                      <Icon size={14} style={{ color: m.color }} />
                      <span>{m.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Text Input */}
            <div className="qwen3tts-text-section">
              <label>Texto a convertir</label>
              <PromptField
                value={text}
                onChange={setText}
                nodeId={id}
                nodeType="qwen3tts"
                label="Texto a locutar"
                enhance={false}
                placeholder="Escribe el texto que deseas convertir a voz..."
                className="qwen3tts-text-input"
                rows={3}
                testId="qwen3tts-text"
              />
            </div>

            {/* Language Selection */}
            <div className="qwen3tts-lang-section">
              <label>Idioma</label>
              <select 
                value={language} 
                onChange={(e) => setLanguage(e.target.value)}
                className="qwen3tts-select"
              >
                {LANGUAGES.map(lang => (
                  <option key={lang.id} value={lang.id}>{lang.label}</option>
                ))}
              </select>
            </div>

            {/* Mode-specific options */}
            {mode === 'custom_voice' && (
              <div className="qwen3tts-speaker-section">
                <label>Voz</label>
                <div className="qwen3tts-speaker-grid">
                  {SPEAKERS.map(s => (
                    <button
                      key={s.id}
                      className={`qwen3tts-speaker-btn ${speaker === s.id ? 'active' : ''}`}
                      onClick={() => setSpeaker(s.id)}
                    >
                      <span className="speaker-icon">{s.gender === 'F' ? '👩' : '👨'}</span>
                      <span>{s.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {mode === 'voice_clone' && (
              <div className="qwen3tts-clone-section">
                <label>Audio de Referencia (3s mínimo)</label>
                {connectedAudio?.outputUrl ? (
                  <div className="qwen3tts-connected-audio">
                    <CheckCircle size={14} />
                    <span>Audio conectado</span>
                  </div>
                ) : (
                  <>
                    <div 
                      className="qwen3tts-upload-zone"
                    >
                      {referenceAudio ? (
                        <div className="qwen3tts-file-selected">
                          <CheckCircle size={16} />
                          <span>{referenceAudio.name}</span>
                        </div>
                      ) : (
                        <>
                          <Upload size={20} />
                          <span>Audio de referencia</span>
                        </>
                      )}
                    </div>
                    <button type="button" className="node-load-btn" onClick={handleSourcePickAudio} data-testid="load-btn-qwen-audio">
                      <Upload size={12} /> Cargar
                    </button>
                  </>
                )}
                <div className="qwen3tts-ref-text">
                  <label>Transcripción (recomendado)</label>
                  <input
                    type="text"
                    value={referenceText}
                    onChange={(e) => setReferenceText(e.target.value)}
                    placeholder="Texto del audio de referencia..."
                    className="qwen3tts-input"
                  />
                </div>
              </div>
            )}

            {mode === 'voice_design' && (
              <div className="qwen3tts-design-section">
                <label>Describe la voz</label>
                <PromptField
                  value={voiceDescription}
                  onChange={setVoiceDescription}
                  nodeId={id}
                  nodeType="qwen3tts"
                  label="Descripción de la voz"
                  placeholder="Ej: Una voz femenina cálida y amigable con acento británico suave"
                  className="qwen3tts-text-input"
                  rows={2}
                  testId="qwen3tts-voice-description"
                />
              </div>
            )}

            {/* Style Instruction (all modes) */}
            <div className="qwen3tts-style-section">
              <label>Estilo/Emoción (opcional)</label>
              <input
                type="text"
                value={styleInstruction}
                onChange={(e) => setStyleInstruction(e.target.value)}
                placeholder="Ej: habla despacio y calmado, tono emocionado"
                className="qwen3tts-input"
              />
            </div>

            {/* Info */}
            <p className="qwen3tts-info">
              10 idiomas • 9 voces • Baja latencia
            </p>

            {/* Process Button */}
            <button
              className="node-process-btn qwen3tts-process-btn"
              onClick={handleProcess}
              disabled={runMissing.length > 0}
              title={requirementsTitle(runMissing)}
              style={{ backgroundColor: currentMode?.color }}
              data-testid="qwen3tts-generate-btn"
            >
              <Volume2 size={14} />
              Generar Voz
            </button>
            <NodeRequirements missing={runMissing} optional={runOptional} />
          </>
        )}

        {/* Error Display */}
        <NodeError error={execError} insufficientCredits={insufficientCredits} />
      </div>
      
      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle"
        isConnectable={!!data.hasOutput}
      />
    </div>
  );
});

Qwen3TTSNode.displayName = 'Qwen3TTSNode';

export default Qwen3TTSNode;
